import React, { useMemo, useState } from 'react';

const normalizeText = (value) => String(value ?? '')
  .replace(/[۰-۹]/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit))
  .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
  .toLowerCase()
  .trim();

export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'انتخاب کنید',
  searchPlaceholder = 'جستجو...',
  getOptionValue = (option) => option?.id,
  getOptionLabel = (option) => option?.name || option?.title || option?.label || String(option?.id ?? ''),
  getOptionSearchText = null,
  className = 'select',
  inputClassName = 'input',
  wrapperClassName = '',
  disabled = false,
  required = false,
  allowEmpty = true,
  emptyLabel,
  noResultsLabel = 'موردی پیدا نشد',
  maxVisibleOptions = 250,
}) {
  const [term, setTerm] = useState('');
  const normalizedTerm = normalizeText(term);

  const filteredOptions = useMemo(() => {
    const list = Array.isArray(options) ? options : [];
    if (!normalizedTerm) return list.slice(0, maxVisibleOptions);
    return list
      .filter((option) => {
        const label = getOptionLabel(option);
        const extra = getOptionSearchText ? getOptionSearchText(option) : '';
        return normalizeText(`${label} ${extra}`).includes(normalizedTerm);
      })
      .slice(0, maxVisibleOptions);
  }, [options, normalizedTerm, getOptionLabel, getOptionSearchText, maxVisibleOptions]);

  const selectedOption = useMemo(() => {
    return (Array.isArray(options) ? options : []).find((option) => String(getOptionValue(option)) === String(value));
  }, [options, value, getOptionValue]);

  const selectedLabel = selectedOption ? getOptionLabel(selectedOption) : '';

  return (
    <div className={`space-y-1 ${wrapperClassName || ''}`.trim()}>
      <input
        type="search"
        className={inputClassName}
        value={term}
        disabled={disabled}
        placeholder={selectedLabel ? `جستجو در لیست؛ انتخاب فعلی: ${selectedLabel}` : searchPlaceholder}
        onChange={(event) => setTerm(event.target.value)}
      />
      <select
        className={className}
        value={value ?? ''}
        onChange={onChange}
        disabled={disabled}
        required={required}
      >
        {allowEmpty ? <option value="">{emptyLabel || placeholder}</option> : null}
        {selectedOption && normalizedTerm && !filteredOptions.some((option) => String(getOptionValue(option)) === String(value)) ? (
          <option value={getOptionValue(selectedOption)}>{getOptionLabel(selectedOption)}</option>
        ) : null}
        {filteredOptions.map((option) => (
          <option key={getOptionValue(option)} value={getOptionValue(option)}>
            {getOptionLabel(option)}
          </option>
        ))}
      </select>
      {normalizedTerm && !filteredOptions.length ? <div className="text-xs text-amber-600">{noResultsLabel}</div> : null}
      {Array.isArray(options) && options.length > maxVisibleOptions && !normalizedTerm ? (
        <div className="text-xs text-slate-500">برای دیدن گزینه‌های بیشتر، داخل کادر بالا جستجو کنید.</div>
      ) : null}
    </div>
  );
}
